import React, { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Mail, RotateCw } from "lucide-react";
import AuthShell from "../../components/AuthShell/AuthShell";
import { useAuth } from "../../services/AuthContext";
import "./auth.css";

const COPY = {
    verify: {
        eyebrow: "Console / Verify email",
        title: "Check your email.",
        lede: (email) =>
            email
                ? `We sent a verification link to ${email}. Click it to activate your account.`
                : "We sent you a verification link. Click it to activate your account.",
        resendLabel: "Resend verification email",
    },
    reset: {
        eyebrow: "Console / Reset password",
        title: "Check your email.",
        lede: (email) =>
            email
                ? `If an account exists for ${email}, a reset link is on its way.`
                : "If an account exists for that email, a reset link is on its way.",
        resendLabel: "Send another reset link",
    },
    magic: {
        eyebrow: "Console / Magic link",
        title: "Check your email.",
        lede: (email) =>
            email
                ? `We sent a one-time sign-in link to ${email}. It expires shortly.`
                : "We sent you a one-time sign-in link. It expires shortly.",
        resendLabel: "Send a new link",
    },
};

function CheckEmailPage() {
    const location = useLocation();
    const { resendVerification, forgotPassword, requestMagicLink } = useAuth();

    const intent = location.state?.intent;
    const email = location.state?.email;
    const [resendBusy, setResendBusy] = useState(false);
    const [resentAt, setResentAt] = useState(null);
    const [resendErr, setResendErr] = useState(null);

    if (!intent) {
        return <Navigate to="/login" replace />;
    }

    const meta = COPY[intent];
    if (!meta) return <Navigate to="/login" replace />;

    const resend = async () => {
        if (!email) return;
        setResendBusy(true);
        setResendErr(null);
        try {
            if (intent === "verify") await resendVerification(email);
            else if (intent === "reset") await forgotPassword(email);
            else if (intent === "magic") await requestMagicLink(email);
            setResentAt(Date.now());
        } catch (err) {
            setResendErr(err?.message || "Could not resend.");
        } finally {
            setResendBusy(false);
        }
    };

    return (
        <AuthShell
            eyebrow={meta.eyebrow}
            title={meta.title}
            lede={meta.lede(email)}
            footer={
                <>
                    <Link to="/login" className="auth-shell-link auth-shell-link--accent">
                        ← Back to sign-in
                    </Link>
                </>
            }
        >
            <div className="auth-check">
                <div className="auth-check__icon" aria-hidden="true">
                    <Mail size={20} />
                </div>
                <p className="auth-check__hint">
                    Don't see it? Check your spam folder, or{" "}
                    <button
                        type="button"
                        className="auth-check__resend"
                        onClick={resend}
                        disabled={!email || resendBusy || resentAt}
                    >
                        <RotateCw size={11} aria-hidden="true" />
                        {resendBusy
                            ? "sending…"
                            : resentAt
                                ? "sent again"
                                : meta.resendLabel.toLowerCase()}
                    </button>
                    .
                </p>
                {resendErr && (
                    <p className="auth-check__error">{resendErr}</p>
                )}
            </div>
        </AuthShell>
    );
}

export default CheckEmailPage;
